// [S-721] Marketplace — browse platform (and, later, 3rd-party) listings,
// import one as an editable L2 draft, and rate a listing.
//
// Sibling to Templates.tsx (the read-only Certified Library browse screen)
// in structure — same filter-chip pattern, same card-grid shape — but this
// is the MANAGEMENT surface: per Diego's model, Certified Library stays
// read-only, Marketplace is where a tenant decides what to import, and
// Template Management is where an imported/custom L2 template gets edited
// and certified.
//
// Import/Remove is a plain toggle, not two independent concepts (2026-08-14
// correction, Diego live-testing on mobile): an earlier version folded
// S-718's per-tenant L1 exclusion into this card too, but "excluded" and
// "imported" read as contradictory sitting side by side on the same card —
// they answer genuinely different questions (is the PLATFORM original
// still visible elsewhere vs. do I have my OWN copy) and conflating them
// confused testing every time. Exclude/Include is Template Management's
// job alone now, same as before S-721. "Remove" here reuses
// TemplateService.deprecate (useDeprecate) — the tenant's own imported
// copy, any lifecycle state -> deprecated — and MarketplaceService.import()
// revives a deprecated copy back to draft on re-import rather than
// treating it as already-imported, so this is a real toggle in both
// directions, not a one-way ratchet.
//
// Cards are NOT tpl__card (see marketplace.css header comment) — a
// Marketplace card has several independent interactive children (Import,
// Remove, rate), so it isn't a single whole-card link.
//
// Per-listing "already imported by my tenant" state has no field on the
// wire response (import count is an ANONYMOUS platform aggregate — see
// MarketplaceService.rate/list doc comments — it can't answer "did I
// import this"). Answered instead by cross-referencing the tenant's own
// L2 template list via useAdminTemplates (excluding deprecated rows — a
// removed import must read back as "not imported"), reusing existing data
// rather than adding a new backend field for something already derivable:
// import() reuses the listing id as the L2 template id, so "a non-
// deprecated template with this id exists in my tenant's own templates" is
// exactly "I've imported this".
//
// The wireframe (docs/Wireframes/Marketplace.html) didn't depict a rating
// SUBMISSION control (only the read-only average) — the interactive star
// row in each card's footer is this screen's own addition to make the
// shipped rate() backend actually reachable from the UI.
import { useMemo, useState } from "react";
import { useSession } from "../auth/AuthProvider";
import { TALENT_TIERS, talentTierLabel } from "../lib/talentTier";
import {
  useImportListing,
  useMarketplaceListings,
  useRateListing,
  type MarketplaceListingFilter,
  type MarketplaceListingRow,
} from "../data/marketplaceQuery";
import { useAdminTemplates, useDeprecate } from "../data/templatesQuery";
import { Alert, Button, PageHead, Pagination, Select, usePagination } from "../components";
import { StarRating } from "../components/marketplace/StarRating";

const DEAL_TYPES: ReadonlyArray<{
  value: NonNullable<MarketplaceListingFilter["dealType"]>;
  label: string;
}> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{
  value: NonNullable<MarketplaceListingFilter["guild"]>;
  label: string;
}> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const DOCUMENT_FORMS: ReadonlyArray<{
  value: NonNullable<MarketplaceListingFilter["documentForm"]>;
  label: string;
}> = [
  { value: "deal_memo", label: "Deal memo" },
  { value: "long_form", label: "Long form" },
];

const DEAL_TYPE_LABELS = new Map(DEAL_TYPES.map((d) => [d.value, d.label]));
const DOCUMENT_FORM_LABELS = new Map(DOCUMENT_FORMS.map((d) => [d.value, d.label]));

export function Marketplace(): JSX.Element {
  const session = useSession();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";

  const [filter, setFilter] = useState<MarketplaceListingFilter>({});
  const listingsQuery = useMarketplaceListings(tenantId, filter);

  // See file header — this is how "already imported" is known per listing.
  // Keyed to the import timestamp (not just a boolean) so the card can show
  // *when* — importedFrom.importedAt is already stamped server-side
  // (MarketplaceService.import), just wasn't surfaced here until now.
  // Excludes deprecated rows: a "Removed" copy must read back as
  // not-imported (see file header), matching MarketplaceService.import()'s
  // revive-on-reimport behavior.
  const ownTemplatesQuery = useAdminTemplates(tenantId, {});
  const importedAtByListingId = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of ownTemplatesQuery.data ?? []) {
      if (
        t.layerSource === "L2" &&
        t.tenantId === tenantId &&
        t.importedFrom != null &&
        t.lifecycleState !== "deprecated"
      ) {
        map.set(t.id, t.importedFrom.importedAt);
      }
    }
    return map;
  }, [ownTemplatesQuery.data, tenantId]);

  const importListing = useImportListing();
  const removeImport = useDeprecate();
  const rateListing = useRateListing();

  const canImport = tenantId.length > 0;
  const canRate = tenantId.length > 0;

  // One vote per person is enforced server-side (MarketplaceService.rate) —
  // this is just immediate UI feedback so the control locks right after a
  // successful submission instead of inviting more clicks that would only
  // no-op. Session-local by design: there's no read endpoint for "have I
  // voted before" (the voter marker is never exposed via any GET), so a
  // page reload can't pre-populate this — the server still blocks the
  // repeat vote either way.
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());

  const rows = listingsQuery.data ?? [];
  const pager = usePagination(rows, 24);

  const isLoading = listingsQuery.isLoading;
  const isFetching = listingsQuery.isFetching;
  const isError = listingsQuery.isError;

  return (
    <>
      <PageHead
        eyebrow={
          <>
            <span className="src-dot cert" aria-hidden="true" />
            Templates · marketplace
          </>
        }
        title={
          <>
            Bring the library <em>into your studio</em>.
          </>
        }
        sub={
          <>
            Browse the platform's certified templates. Import one to fork an
            editable copy into your own library, any time.
          </>
        }
      />

      <FilterBar filter={filter} onChange={setFilter} count={rows.length} />

      {(isLoading || (isFetching && rows.length === 0)) && (
        <div className="tpl__state" role="status" aria-live="polite">
          Loading marketplace listings…
        </div>
      )}

      {isError && (
        <Alert label="Couldn't load the marketplace" tone="error">
          {listingsQuery.error?.message ?? "Unknown error"}
        </Alert>
      )}

      {!isLoading && !isFetching && !isError && rows.length === 0 && (
        <div className="mkt__empty">
          <div className="mkt__empty__eyebrow">No listings match your filters</div>
          <div className="mkt__empty__hint">
            Try clearing a filter — every certified platform template is
            browsable here.
          </div>
          <Button variant="ghost" onClick={() => setFilter({})}>
            Clear filters
          </Button>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <div className="mkt__grid" role="list">
            {pager.pageItems.map((listing) => (
              <MarketplaceCard
                key={listing.id}
                listing={listing}
                importedAt={importedAtByListingId.get(listing.id) ?? null}
                canImport={canImport}
                canRate={canRate}
                voted={votedIds.has(listing.id)}
                importing={
                  importListing.isPending &&
                  importListing.variables?.listingId === listing.id
                }
                removing={
                  removeImport.isPending &&
                  removeImport.variables?.templateId === listing.id
                }
                onImport={() => importListing.mutate({ tenantId, listingId: listing.id })}
                onRemove={() =>
                  removeImport.mutate({
                    templateId: listing.id,
                    tenantId,
                    asOf: new Date().toISOString(),
                    successorId: null,
                  })
                }
                onRate={(rating) =>
                  rateListing.mutate(
                    { tenantId, listingId: listing.id, rating },
                    {
                      onSuccess: () =>
                        setVotedIds((prev) => new Set(prev).add(listing.id)),
                    }
                  )
                }
              />
            ))}
          </div>
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
            total={pager.total}
            rangeStart={pager.rangeStart}
            rangeEnd={pager.rangeEnd}
            itemLabel="listing"
          />
        </>
      )}
    </>
  );
}

type FilterBarProps = {
  filter: MarketplaceListingFilter;
  onChange: (next: MarketplaceListingFilter) => void;
  count: number;
};

function FilterBar({ filter, onChange, count }: FilterBarProps): JSX.Element {
  return (
    <div className="tpl__filters">
      <span className="tpl__filterlbl">Filter</span>

      <FilterSelect
        label="Deal type"
        value={filter.dealType}
        options={DEAL_TYPES}
        onChange={(value) => onChange({ ...filter, dealType: value })}
      />
      <FilterSelect
        label="Guild"
        value={filter.guild}
        options={GUILDS}
        onChange={(value) => onChange({ ...filter, guild: value })}
      />
      <FilterSelect
        label="Talent tier"
        value={filter.tier}
        options={TALENT_TIERS}
        onChange={(value) => onChange({ ...filter, tier: value })}
      />
      <FilterSelect
        label="Document form"
        value={filter.documentForm}
        options={DOCUMENT_FORMS}
        onChange={(value) => onChange({ ...filter, documentForm: value })}
      />

      <span className="tpl__count">
        {count.toLocaleString()} listing{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

function FilterSelect<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V | undefined;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (value: V | undefined) => void;
}): JSX.Element {
  const isOn = value !== undefined;
  return (
    <span className={isOn ? "tpl__filter is-on" : "tpl__filter"}>
      <Select
        label={label}
        value={value}
        options={options}
        onValueChange={onChange}
        triggerClassName="tpl__filter__select"
      />
      {isOn && (
        <Button
          variant="bare"
          className="tpl__filter__clear"
          aria-label={`Clear ${label}`}
          onClick={() => onChange(undefined)}
        >
          ×
        </Button>
      )}
    </span>
  );
}

function MarketplaceCard({
  listing,
  importedAt,
  canImport,
  canRate,
  voted,
  importing,
  removing,
  onImport,
  onRemove,
  onRate,
}: {
  listing: MarketplaceListingRow;
  /** ISO timestamp when this tenant imported the listing, or null if they
   *  haven't. (importedFrom.importedAt, stamped server-side at import.) */
  importedAt: string | null;
  canImport: boolean;
  canRate: boolean;
  voted: boolean;
  importing: boolean;
  removing: boolean;
  onImport: () => void;
  onRemove: () => void;
  onRate: (rating: number) => void;
}): JSX.Element {
  const title =
    listing.title ?? `${listing.guild ?? ""} ${listing.dealType ?? ""}`.trim();
  const ratingLabel =
    listing.averageRating !== null
      ? `${listing.averageRating.toFixed(1)} out of 5`
      : "Not yet rated";

  return (
    <div className="mkt__card" role="listitem" data-testid={`mkt-card-${listing.id}`}>
      <div className="mkt__card__head">
        <span className="mkt__card__id">{listing.id}</span>
        <span className="mkt__card__head__badges">
          <span className="mkt__badge mkt__badge--platform">
            {listing.publisherId === null ? "Platform" : "Contributor"}
          </span>
        </span>
      </div>
      <div className="mkt__card__title">{title}</div>
      <div className="mkt__card__chips">
        {listing.dealType !== undefined && (
          <span>{DEAL_TYPE_LABELS.get(listing.dealType) ?? listing.dealType}</span>
        )}
        {listing.guild !== undefined && <span>{listing.guild}</span>}
        {listing.tier !== undefined && <span>{talentTierLabel(listing.tier)}</span>}
        {listing.documentForm !== null && listing.documentForm !== undefined && (
          <span>{DOCUMENT_FORM_LABELS.get(listing.documentForm) ?? listing.documentForm}</span>
        )}
      </div>

      {/* ONE star row — shows the average at rest, previews an integer
          rating on hover when interactive, submits on click. Never a
          separate read-only row plus a separate input row (see
          StarRating.tsx header comment for why that was wrong). */}
      <div className="mkt__card__stats">
        <StarRating
          value={listing.averageRating ?? 0}
          interactive={canRate && !voted}
          onChange={onRate}
          ariaLabel={
            voted
              ? `You've rated this listing. Current average: ${ratingLabel}`
              : `${ratingLabel} — click a star to rate`
          }
        />
        {listing.averageRating !== null ? (
          <>
            <span>{listing.averageRating.toFixed(1)}</span>
            <span>({listing.ratingCount.toLocaleString()})</span>
          </>
        ) : (
          <span>Not yet rated</span>
        )}
        <span>
          · {listing.importCount.toLocaleString()} import
          {listing.importCount === 1 ? "" : "s"}
        </span>
        {voted && <span className="mkt__caption">You rated this</span>}
      </div>

      <div className="mkt__card__actions">
        {importedAt !== null ? (
          <span
            className="mkt__imported"
            title={new Date(importedAt).toLocaleString()}
          >
            ✓ Imported {new Date(importedAt).toLocaleDateString()}
          </span>
        ) : (
          <Button
            variant="primary"
            size="xs"
            disabled={!canImport}
            loading={importing}
            onClick={onImport}
          >
            Import
          </Button>
        )}
        {/* Import/Remove is a plain toggle (2026-08-14 correction) — the
            opposite of "Import" is "Remove", not a separate concept. */}
        {importedAt !== null && (
          <Button
            variant="bare"
            className="mkt__removetoggle"
            loading={removing}
            onClick={onRemove}
            data-testid={`mkt-remove-${listing.id}`}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
